import { useState, useEffect, useMemo } from "react";
import Wrapper from "../../../components/helpers/Wrapper";
import PageHeader from "../../../components/layout/pageHeader";
import SelectedGlobalFilter from "../../../components/ui/SelectedGlobalFilter";
import Card from "../../../components/ui/Card";
import Slider from "../../../components/ui/Slider";
import { SwiperSlide } from "swiper/react";
import {
    createLoaderSection,
    createErrorLoaderSection,
    createNoDataLoaderSection
} from "../../../components/ui/states/States";
import useFetch from "../../../hooks/useFetch";
import ErrorBoundary from "../../../utils/ErrorBoundary";
import ExportDropdown from "../../../components/ui/dropdown/ExportDropdown";
import Loader from "../../../components/ui/loaders/Loader";
import Datatable from "../../../components/ui/tables/Datatable";
import Dropdown from "../../../components/ui/dropdown/Dropdown";
import CrossSourceLineChart from "../../../components/ui/charts/CrossSourceLineChart";
import { useSelector } from "react-redux";
import { ReactComponent as TableIcon } from "../../../assets/icons/icons-svg/Table-icon.svg";
import { ReactComponent as ChartIcon } from "../../../assets/icons/icons-svg/Chart-icon.svg";
import MapComponent from '../../../pageComponents/MapComponent';

// --- CrossSourceSalesPerformance Component ---
const filterKeyMap = {
    country: "country_id",
    store_type: "store_type_id",
    category: "category_id",
    sub_category: "sub_category_id",
    segment: "segment_id",
    form: "form_id",
    manufacture: "manufacture_id",
    brand: "brand_id",
    variant: "variant_id",
    pack_size: "pack_size_id",
    promotion: "promotion_id",
    sku: "sku_id",
};

const NoDataAvailableChecker = (data) => {
    if (!data) return true;
    if (Array.isArray(data)) return data.length === 0;
    return Object.keys(data).length === 0;
};

const CrossSourceSalesPerformance = () => {
    const api_version = "-v1";
    const crossSelectedFilters = useSelector((state) => state.globalFilter.crossSelectedFilters);

    // Get date values from Redux
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Map UI filter keys to API keys and build payload
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(crossSelectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [crossSelectedFilters]);

    // Memoize payload so it updates on any filter/date change
    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters,
        },
    }), [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    const { post } = useFetch();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch data whenever payload changes (dates or filters)
    useEffect(() => {
        setIsLoading(true);
        post(`ri/cross-source/summary${api_version}`, payload)
            .then(response => {
                setData(response?.data || response);
                setError(null);
            })
            .catch(err => {
                setError(err);
                setData(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [payload, post, api_version]);

    let content;
    if (isLoading) {
        content = createLoaderSection('cardLoader', "Cross Source Sales Performance", 'chartCard');
    } else if (error) {
        content = createErrorLoaderSection('cardType');
    } else if (!isLoading && (!data || data.length === 0 || NoDataAvailableChecker(data))) {
        content = createNoDataLoaderSection('cardType');
    } else {
        const CARD_DATA = data || [];
        content = (
            <Slider sliderTitle="Summary">
                {CARD_DATA.map((data, index) => (
                    <SwiperSlide key={index}>
                        <Card
                            module="summary"
                            section="cross_source_sales_performance"
                            variant="chartCard"
                            cardTitle={data.label}
                            currentValue={data.currentValue}
                            previousValue={data.previousValue}
                            chartType="Line"
                            chartData={data.subData?.data}
                            chartDataLabels={data.subData?.dimension}
                        />
                    </SwiperSlide>
                ))}
            </Slider>
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                {content}
            </ErrorBoundary>
        </Wrapper>
    );
};
// --- End CrossSourceSalesPerformance Component ---

// const getRandomColor = () => {
//     const letters = "0123456789ABCDEF";
//     let color = "#";
//     for (let i = 0; i < 6; i++) {
//         color += letters[Math.floor(Math.random() * 16)];
//     }
//     return color;
// };
const chartColors = ["#11A1FD", "#5A75F9", "#07C180"];

const valueDropdownOptions = [
    { label: "Sales Value", value: "sales_value" },
    // { label: "Sales Volume", value: "sales_volume" },
    { label: "Sales Unit", value: "sales_unit" }
];

const granularityDropdownOptions = [
    { label: "Month", value: "month_no" },
    { label: "Quarter", value: "quarter_no" },
    { label: "Year", value: "year_no" }
];

const IntegratedDataSourceAnalysis = () => {
    const api_version = "-v1";
    const [toggleTable, setToggleTable] = useState(false);
    const [valueDropdown, setValueDropdown] = useState(valueDropdownOptions[0]);
    const [granularityDropdown, setGranularityDropdown] = useState(granularityDropdownOptions[0]);
    const [chart, setChart] = useState({
        chartData: [],
        chartLabels: [],
        legend: []
    });

    // Store all table data here
    const [rawTableData, setRawTableData] = useState({ results: [], headers: [], page: {} });

    // Pagination, sorting, search config
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 10,
        search_value: "",
        order_by: "",
        order_type: "desc"
    });

    const [hiddenSeries, setHiddenSeries] = useState([]);

    const { post, isLoading, error } = useFetch();
    const crossSelectedFilters = useSelector((state) => state.globalFilter.crossSelectedFilters);
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);
    // Map UI filter keys to API keys and build payload
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(crossSelectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [crossSelectedFilters]);

    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate
            },
            filter: apiGlobalFilters
        },
        section_filter: {
            value_dropdown: valueDropdown.value,
            granularity_dropdown: granularityDropdown.value
        }
        // No pagination here, we do client-side
    }), [startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters,
        valueDropdown,
        granularityDropdown]);

    const fetchChartData = async () => {
        const response = await post(`/ri/cross-source/integrated-data-source-analysis${api_version}`, payload);

        if (response?.line_chart_data) {
            const { dimension, data, legend } = response.line_chart_data;

            const chartLabels = data.map((item) => item.label);
            const chartData = dimension.map((dim, idx) => ({
                label: dim.label,
                data: data.map((item) => item[dim.value]),
                borderColor: chartColors[idx % chartColors.length],
                backgroundColor: "transparent",
                tension: 0.4,
                legendLabel: legend?.[idx]?.label || dim.label,
                legendColor: chartColors[idx % chartColors.length],
                value: dim.value
            }));

            setChart({ chartData, chartLabels, legend: legend || [] });
        } else {
            setChart({ chartData: [], chartLabels: [], legend: [] });
        }
    };

    const fetchTableData = async () => {
        const response = await post(`/ri/cross-source/integrated-data-source-analysis-table${api_version}`, payload);

        if (response?.data && response?.label) {
            const results = response.data || [];
            const headers = response.label.map((header) => ({
                name: header.name,
                field: header.field,
            }));
            setRawTableData({ results, headers, page: response.pagination || {} });
        } else {
            setRawTableData({ results: [], headers: [], page: {} });
        }
    };

    useEffect(() => {
        toggleTable ? fetchTableData() : fetchChartData();
        // eslint-disable-next-line
    }, [payload, toggleTable]);

    // --- Client-side: filter, sort, paginate table data ---
    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "desc";

    let filtered = rawTableData.results || [];
    if (searchValue) {
        filtered = filtered.filter(row =>
            Object.values(row).some(val =>
                String(val || "").toLowerCase().includes(searchValue.toLowerCase())
            )
        );
    }
    if (orderBy) {
        filtered = filtered.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            } else {
                return orderType === "asc"
                    ? String(aValue).localeCompare(String(bValue))
                    : String(bValue).localeCompare(String(aValue));
            }
        });
    }
    const totalRecords = filtered.length;
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startIdx = (pageNum - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginatedData = filtered.slice(startIdx, endIdx);

    const handleTableConfigs = (newConfig) => {
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const handleLegendClick = (seriesLabel) => {
        setHiddenSeries((prev) =>
            prev.includes(seriesLabel)
                ? prev.filter((l) => l !== seriesLabel)
                : [...prev, seriesLabel]
        );
    };

    const visibleChartData = chart.chartData.filter(
        (series) => !hiddenSeries.includes(series.label)
    );

    const renderLegend = () => {
        const legendItems = chart.legend.length
            ? chart.legend
            : chart.chartData.map((s, idx) => ({
                label: s.label,
                color: chartColors[idx % chartColors.length]
            }));

        // Show all legend items (all visible series)
        return (
            <div className="custom-legend-container" style={{ display: "flex", gap: 24, marginTop: 16, justifyContent: "center" }}>
                {legendItems.map((item, idx) => {
                    const color = item.color || chartColors[idx % chartColors.length];
                    const label = item.label || chart.chartData[idx]?.label || "";
                    const isHidden = hiddenSeries.includes(label);
                    return (
                        <div
                            key={label}
                            className={`custom-legend-item${isHidden ? " legend-disabled" : ""}`}
                            style={{ display: "flex", alignItems: "center", cursor: "pointer", opacity: isHidden ? 0.5 : 1 }}
                            onClick={() => handleLegendClick(label)}
                        >
                            <span
                                style={{
                                    display: "inline-block",
                                    width: 18,
                                    height: 6,
                                    borderRadius: 3,
                                    background: color,
                                    marginRight: 8,
                                    border: "1px solid #e0e0e0"
                                }}
                            />
                            <span style={{ fontSize: 14, color: "#213345" }}>{label}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderChart = () => {
        if (isLoading) return <Loader loaderType="stackedColumnChartLoader" />;
        if (error) return createErrorLoaderSection("stackedColumnChart");
        // If there is truly no data at all
        if (!chart.chartData.length || NoDataAvailableChecker(chart.chartData)) return createNoDataLoaderSection("stackedColumnChart");

        // If all legends are disabled, show chart with all series faded
        const allLegendsDisabled = visibleChartData.length === 0;
        return (
            <ErrorBoundary>
                <div style={allLegendsDisabled ? { opacity: 0.2, pointerEvents: 'none' } : {}}>
                    <CrossSourceLineChart
                        chartClass="section-chart-wrapper"
                        chartData={chart.chartData}
                        chartLabels={chart.chartLabels}
                        chartId={`integratedDataSourceChart-${Math.random()}`}
                        cardTitle="Integrated Data Source Analysis"
                        showScrollbar={true}
                        visibleChartData={allLegendsDisabled ? chart.chartData : visibleChartData}
                    />
                </div>
                {renderLegend()}
            </ErrorBoundary>
        );
    };

    const renderTable = () => {
        if (isLoading) return <Loader loaderType="smallTableLoader" />;
        if (error) return createErrorLoaderSection("smallTableLoader");
        if (!filtered.length || NoDataAvailableChecker(filtered)) return createNoDataLoaderSection("smallTableLoader");

        return (
            <Datatable
                tableData={paginatedData}
                tableHeader={rawTableData.headers}
                tableLength={pageSize}
                tableConfigs={paginationConfig}
                setTableConfigs={handleTableConfigs}
                tablePaginationData={{
                    page_number: pageNum,
                    total_pages: totalPages,
                    total_records: totalRecords,
                    record_end: Math.min(pageNum * pageSize, totalRecords)
                }}
                searchPlaceHolder="Search data..."
                showPagination={true}
                hideBoxShadow={true}
            />
        );
    };

    return (
        <Wrapper>
            <ErrorBoundary>
                <div id="integrated_data_source_section" className="grid grid_cols_4 grid_margin_bottom fullpage_section_card section_card">
                    <div className="grid col_span_4">
                        <div className="section_card_header">
                            <h2 className="section_card_title">Integrated Data Source Analysis</h2>
                            <div data-html2canvas-ignore>
                                <Dropdown
                                    placeholder="Value"
                                    options={valueDropdownOptions}
                                    value={valueDropdown}
                                    selectedOptions={valueDropdown}
                                    setStatedropdown={setValueDropdown}
                                    className="grid_margin_right"
                                />
                                {/* <Dropdown
                                    placeholder="Granularity"
                                    options={granularityDropdownOptions}
                                    value={granularityDropdown}
                                    selectedOptions={granularityDropdown}
                                    setStatedropdown={setGranularityDropdown}
                                    className="grid_margin_right"
                                /> */}
                                <span className="custom-tooltip-container">
                                    <button
                                        type="button"
                                        className="icon-toggle-btn"
                                        onClick={() => setToggleTable((prev) => !prev)}
                                        aria-label={toggleTable ? "View Chart" : "View Table"}
                                        style={{ marginRight: 8 }}
                                    >
                                        {toggleTable ? <ChartIcon /> : <TableIcon />}
                                    </button>
                                    <span className="custom-tooltip">
                                        {toggleTable ? "View Chart" : "View Table"}
                                    </span>
                                </span>
                                <ExportDropdown
                                    sectionId="integrated_data_source_section"
                                    sectionName="Integrated Data Source Analysis"
                                    sectionData={toggleTable ? filtered : chart.chartData}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="grid col_span_4">
                        <div className="section_card_body">
                            <div className="section_card_chart_wrapper">
                                {toggleTable ? renderTable() : renderChart()}
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Wrapper>
    );
};


const CrossSource = () => {
    // Map panel state and logic (copied and adapted from Summary.jsx)
    const [showMapPanel, setShowMapPanel] = useState(false);
    const [mapGeoJson, setMapGeoJson] = useState(null);
    const [mapLoading, setMapLoading] = useState(false);
    const [mapError, setMapError] = useState(null);
    const [hoverData, setHoverData] = useState({});
    const [hoverLoading, setHoverLoading] = useState(false);
    const [hoverError, setHoverError] = useState(null);

    // Get date and global filters from Redux
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);
    const crossSelectedFilters = useSelector((state) => state.globalFilter.crossSelectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(crossSelectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [crossSelectedFilters]);
    const mapPayload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        }
    }), [startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate, apiGlobalFilters]);
    const { post } = useFetch();

    // Listen for custom event from sidebar map button
    useEffect(() => {
        const handler = () => setShowMapPanel(true);
        window.addEventListener('open-map-panel', handler);
        return () => window.removeEventListener('open-map-panel', handler);
    }, []);

    // Fetch map data and hover data when map panel opens
    useEffect(() => {
        if (!showMapPanel) return;
        setMapLoading(true);
        setMapError(null);
        setHoverLoading(true);
        setHoverError(null);
        // Fetch map coordinates
        post('ri/map-coordinates', mapPayload)
            .then(response => {
                // Convert response to GeoJSON
                const features = Array.isArray(response)
                    ? response.map(item => ({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [item.longitude, item.latitude]
                        },
                        properties: {
                            ...item,
                            name: item.country_name || item.country_code
                        }
                    }))
                    : [];
                setMapGeoJson({ type: 'FeatureCollection', features });

                // Prepare country_ids for hover API
                const countryIds = features.map(f => f.properties.country_id).filter(Boolean);
                if (countryIds.length > 0) {
                    // Call hover API for all countries
                    const hoverPayload = {
                        global_filter: {
                            date_filter: mapPayload.global_filter.date_filter,
                            filter: {
                                ...mapPayload.global_filter.filter,
                                country_id: countryIds
                            }
                        }
                    };
                    post('ri/map-hover', hoverPayload)
                        .then(hoverResponse => {
                            // Store hover data by country_id
                            if (Array.isArray(hoverResponse)) {
                                const hoverDataObj = {};
                                hoverResponse.forEach(d => {
                                    if (d.country_id) hoverDataObj[d.country_id] = d;
                                });
                                setHoverData(hoverDataObj);
                            }
                        })
                        .catch(err => {
                            setHoverError(err);
                        })
                        .finally(() => setHoverLoading(false));
                } else {
                    setHoverData({});
                    setHoverLoading(false);
                }
            })
            .catch(err => {
                setMapError(err);
                setMapGeoJson(null);
                setHoverLoading(false);
            })
            .finally(() => setMapLoading(false));
    }, [showMapPanel, post, mapPayload]);

    // Hover handler for map markers
    const handleMarkerHover = (properties) => {
        if (!properties || !properties.country_id) return;
        setHoverLoading(true);
        setHoverError(null);
        // Add country_id to filter for hover
        const hoverPayload = {
            global_filter: {
                date_filter: mapPayload.global_filter.date_filter,
                filter: {
                    ...mapPayload.global_filter.filter,
                    country_id: [properties.country_id]
                }
            }
        };
        post('ri/map-hover', hoverPayload)
            .then(response => {
                // Response is array, find matching country_id
                if (Array.isArray(response)) {
                    const data = response.find(d => d.country_id === properties.country_id);
                    setHoverData(prev => ({ ...prev, [properties.country_id]: data }));
                }
            })
            .catch(err => {
                setHoverError(err);
            })
            .finally(() => setHoverLoading(false));
    };

    return (
        <Wrapper>
            <div className="dashboard-container">
                {/* LEFT: Map (only visible if showMapPanel) */}
                <div
                    className="dashboard-leftside"
                    style={
                        showMapPanel
                            //  ? { width: 400, minWidth: 320, maxWidth: 440, transition: 'width 0.3s' }
                            //  : { width: 0, minWidth: 0, maxWidth: 0, transition: 'width 0.3s', overflow: 'hidden', padding: 0 }

                            ? { flexBasis: 400, minWidth: 320, maxWidth: 440, transition: 'flex-basis 0.8s cubic-bezier(0.4,0,0.2,1)' }
                            : { flexBasis: 0, minWidth: 0, maxWidth: 0, transition: 'flex-basis 0.8s cubic-bezier(0.4,0,0.2,1)', overflow: 'hidden', padding: 0 }
                    }
                >
                    {showMapPanel && (
                        <div
                            className="dashboard-left map-panel-ubuntu map-panel-custom"

                        >
                            {/* Header */}
                            <div className="map-panel-header">
                                <span className="map-panel-title">UAE</span>
                                <button
                                    onClick={() => setShowMapPanel(false)}
                                    className="map-panel-close-btn"
                                    aria-label="Close Map"
                                >
                                    ×
                                </button>
                            </div>
                            {/* Map */}
                            <div className="map-panel-map-container">
                                {(mapLoading || hoverLoading) && (
                                    <div className="map-panel-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
                                        <img
                                            src={require('../../../assets/images/gcc-map-loader.png')}
                                            alt="Loading map..."
                                            style={{ width: 240, height: 'auto', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}
                                {(mapError || hoverError) && (
                                    <div className="map-panel-error">Error loading map data</div>
                                )}
                                {!mapLoading && !hoverLoading && mapGeoJson && (
                                    <MapComponent
                                        geoJson={mapGeoJson}
                                        mapLevel={1}
                                        northPoint={["31.273904, 34.525594"]}
                                        southPoint={["60.695581, 13.951499"]}
                                        centerPoint={["44.556137,23.785713"]}
                                        zoomLevel={2.5}
                                        onChange={() => { }}
                                        toolTipValues={["country_name", "country_code"]}
                                        toolTipLabels={["Country Name", "Country Code"]}
                                        growthDegrowth={null}
                                        onMarkerHover={handleMarkerHover}
                                        hoverData={hoverData}
                                        filterType="cross" // <-- Ensure cross filter is used
                                    />
                                )}
                            </div>
                            {/* Legend */}
                            <div className="map-panel-legend">
                                <span className="map-panel-legend-item">
                                    <span className="legend-dot green"></span>
                                    Sales Value Growth
                                </span>
                                <span className="map-panel-legend-item">
                                    <span className="legend-dot red"></span>
                                    Sales Value De-Growth
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT: Dashboard content */}
                <div className="dashboard-right"
                    style={{
                        flex: 1,
                        minWidth: 0
                    }}>
                    <SelectedGlobalFilter />
                    <div className="content">
                        <div className="content_section">
                            <PageHeader pageTitle="Cross Source" />
                            <div className="page_content flex column gap_1rem">
                                <CrossSourceSalesPerformance />
                                <IntegratedDataSourceAnalysis />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Inline CSS for layout and legend */}
            {/* <style>{`
                .dashboard-container {
                  display: flex;
                  gap: 24px;
                }
                .dashboard-leftside {
                  position: sticky;
                  top: 24px;
                  align-self: flex-start;
                  z-index: 10;
                }
                .dashboard-left {
                  background: #fff;
                  border-radius: 16px;
                  box-shadow: 0 2px 16px #0002;
                  width: 400px;
                  min-width: 320px;
                  max-width: 440px;
                  padding: 0;
                  display: flex;
                  flex-direction: column;
                  align-items: stretch;
                  height: calc(100vh - 64px);
                }
                .dashboard-right {
                  flex: 1;
                }
                .map-legend {
                  margin-top: 12px;
                  display: flex;  
                  gap: 16px;
                  align-items: center;
                }
                .legend-dot {
                  width: 16px;
                  height: 16px;
                  border-radius: 50%;
                  display: inline-block;
                  margin-right: 6px;
                }
                .legend-dot.green { background: #06a57d; }
                .legend-dot.red { background: #b51c2a; }
                .map-panel-ubuntu {
                  font-family: Ubuntu, sans-serif !important;
                }
                .map-panel-ubuntu * {
                  font-family: Ubuntu, sans-serif !important;
                }
                .map-panel-ubuntu .legend-dot {
                  width: 14px !important;
                  height: 14px !important;
                  border-radius: 50%;
                  display: inline-block;
                  margin-right: 6px;
                }
                .map-panel-ubuntu .legend-dot.green { background: #06a57d; }
                .map-panel-ubuntu .legend-dot.red { background: #b51c2a; }
                .map-panel-ubuntu .map-legend {
                  font-size: 12px !important;
                }
            `}</style> */}
        </Wrapper>
    );
};

export default CrossSource;